/**
 * Main App Component with Routing
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTranslation } from 'react-i18next';

import Login from './components/Login';
import ChatInterface from './components/ChatInterface';
import AppLayout from './components/AppLayout';
import ChatPage from './pages/ChatPage';
import AdminPage from './pages/AdminPage';
import RAGPage from './pages/RAGPage';
import UserRAGPage from './pages/UserRAGPage';
import SettingsPage from './pages/SettingsPage';
import APIKeySettingsPage from './pages/APIKeySettingsPage';
import AgentPromptsSettingsPage from './pages/AgentPromptsSettingsPage';
import MCPConfigurationPage from './pages/MCPConfigurationPage';
import LogViewerPage from './pages/LogViewerPage';
import useAuthStore from './store/authStore';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#2e7d32',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Protected Route for Guest users only
function ProtectedGuestRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only GUEST role can access
  if (user?.role !== 'GUEST' && user?.department !== 'GUEST') {
    return <Navigate to="/chat" replace />;
  }

  return children;
}

// Protected Route with conditional layout based on role
function ProtectedChatRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is ADMIN or MASTER
  const isAdminOrMaster = user?.role === 'ADMIN' || user?.role === 'MASTER';

  // Admin/Master: Use new AppLayout with sidebar menu
  if (isAdminOrMaster) {
    return <AppLayout><ChatPage /></AppLayout>;
  }

  // Regular users: Use old ChatInterface (full-featured)
  return <ChatInterface />;
}

// Protected Route for Admin pages (Dashboard, etc.)
function ProtectedAdminRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only ADMIN or MASTER can access
  const isAdminOrMaster = user?.role === 'ADMIN' || user?.role === 'MASTER';

  if (!isAdminOrMaster) {
    // Redirect regular users to chat
    return <Navigate to="/chat" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

// Protected Route for Master only pages (Settings)
function ProtectedMasterRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only MASTER can access
  if (user?.role !== 'MASTER') {
    return <Navigate to="/chat" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}


function App() {
  const { i18n } = useTranslation();

  return (
    <ThemeProvider theme={{ ...theme, direction: i18n.dir() }}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/chat"
            element={<ProtectedChatRoute />}
          />
          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute>
                <AdminPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/rag"
            element={
              <ProtectedAdminRoute>
                <RAGPage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedMasterRoute>
                <SettingsPage />
              </ProtectedMasterRoute>
            }
          />
          <Route
            path="/settings/api-keys"
            element={
              <ProtectedMasterRoute>
                <APIKeySettingsPage />
              </ProtectedMasterRoute>
            }
          />
          <Route
            path="/settings/agent-prompts"
            element={
              <ProtectedMasterRoute>
                <AgentPromptsSettingsPage />
              </ProtectedMasterRoute>
            }
          />
          <Route
            path="/settings/mcp-configuration"
            element={
              <ProtectedMasterRoute>
                <MCPConfigurationPage />
              </ProtectedMasterRoute>
            }
          />
          <Route
            path="/settings/logs"
            element={
              <ProtectedMasterRoute>
                <LogViewerPage />
              </ProtectedMasterRoute>
            }
          />
          <Route
            path="/user-rag"
            element={
              <ProtectedGuestRoute>
                <UserRAGPage />
              </ProtectedGuestRoute>
            }
          />
          <Route path="/" element={<Navigate to="/chat" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;

